/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { resolveCssColor } from './ColorTools.js';
import { CssTools } from './CssTools.js';
import { createValueFormat } from './ValueFormat.js';
/**
 * Holds and creates threshold related data.
 */

export class Thresholds {
  constructor() {
    this._thresholds = [];
  }

  /**
   * The thresholds to be shown
   */
  get thresholds() {
    return this._thresholds;
  }

  set thresholds(value) {
    this._thresholds = value;
  }
  /**
   * Creates an array of threshold objects whose label contains their formatted value
   *
   * @param forHorizontalChart - whether the chart that contains the thresholds is horizontal or vertical
   * @param shorthandLabels - use abbreviations for big numbers for value-labels (e.g. k=kilo=1000, m=milli=1/1000, etc.)
   * @param decimalPlaces - The number of decimal places to use. Possible values are from 0 to 10. If undefined no fixed number of decimal places will be used.
   * @returns an array of threshold objects whose label contains their formatted value
   */


  createFormattedThresholds(forHorizontalChart, shorthandLabels, decimalPlaces) {
    const valueFormat = createValueFormat(shorthandLabels, decimalPlaces);
    const formattedValues = this.thresholds.map(threshold => valueFormat(threshold.value));
    return this.thresholds.map((threshold, index) => ({
      value: threshold.value,
      label: threshold.label + (forHorizontalChart ? ' ' : '\n') + formattedValues[index]
    }));
  }
  /**
   * Creates a style object for thresholds
   *
   * @param forHorizontalChart - whether the chart that contains the thresholds is horizontal or vertical
   * @param hostFontFamily - the font family to be used for the threshold labels
   */


  createThresholdStyle(forHorizontalChart, hostFontFamily) {
    const thresholdColor = resolveCssColor('var(--davinci-color-background-elements-4)');
    const style = {
      labelColor: thresholdColor,
      labelFont: {
        family: hostFontFamily,
        size: CssTools.REM2PIXEL(Thresholds.THRESHOLD_LABEL_FONT_SIZE),
        style: 'normal',
        weight: Thresholds.THRESHOLD_LABEL_FONT_WEIGHT
      },
      lineStyle: {
        color: thresholdColor,
        type: 'LargeDashes',
        width: 1.0
      }
    };

    if (this.thresholds.length > 0) {
      if (forHorizontalChart) {
        style.labelSize = CssTools.REM2PIXEL(Thresholds.THRESHOLD_LABEL_HEIGHT);
        style.labelGap = CssTools.REM2PIXEL(Thresholds.THRESHOLD_LABEL_GAP_HORIZONTAL);
      } else {
        style.labelSize = CssTools.REM2PIXEL(Thresholds.THRESHOLD_LABEL_WIDTH);
        style.labelGap = CssTools.REM2PIXEL(Thresholds.THRESHOLD_LABEL_GAP_VERTICAL);
      }
    } else {
      style.labelSize = 0.0;
      style.labelGap = 0.0;
    }

    return style;
  }

}
Thresholds.THRESHOLD_LABEL_WIDTH = 3.0;
Thresholds.THRESHOLD_LABEL_GAP_VERTICAL = 0.25;
Thresholds.THRESHOLD_LABEL_HEIGHT = 2.0;
Thresholds.THRESHOLD_LABEL_GAP_HORIZONTAL = 0.125;
Thresholds.THRESHOLD_LABEL_FONT_WEIGHT = 600;
Thresholds.THRESHOLD_LABEL_FONT_SIZE = 0.75;
